import React, { useState } from "react";
import { Loader2, Columns2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { waitTask } from "@/components/voiceKit";

const options = (voices) => voices.flatMap((v) => v.clone_id
  ? [{ key: `${v.id}:clone`, v, source: "clone", label: `${v.name} (مستنسخ)` }, { key: `${v.id}:preset`, v, source: "preset", label: `${v.name} (أساسي)` }]
  : [{ key: `${v.id}:preset`, v, source: "preset", label: v.name }]);

const render = async (o, text) => {
  const { data } = await api.post("/admin/voices/preview", { preset: o.v.preset, source: o.source, voice_id: o.v.id, text, settings: o.v.settings });
  const st = await waitTask(data.task_id);
  const r = await api.post("/admin/voices/fx", { raw_id: st.raw_id, fx: o.v.fx }, { responseType: "blob" });
  return URL.createObjectURL(r.data);
};

export const VoiceCompare = ({ voices, sampleText }) => {
  const [text, setText] = useState(sampleText);
  const [picked, setPicked] = useState([]);
  const [res, setRes] = useState({});
  const opts = options(voices);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const run = async () => {
    if (picked.length < 2) return toast.error("اختر صوتين على الأقل للمقارنة");
    const sel = opts.filter((o) => picked.includes(o.key));
    setRes(Object.fromEntries(sel.map((o) => [o.key, { busy: true, label: o.label }])));
    await Promise.all(sel.map(async (o) => {
      try { const url = await render(o, text); setRes((r) => ({ ...r, [o.key]: { url, label: o.label } })); }
      catch (e) { setRes((r) => ({ ...r, [o.key]: { error: e.message || "تعذّر", label: o.label } })); }
    }));
  };
  const busy = Object.values(res).some((x) => x.busy);
  return (
    <div className="border border-border rounded-xl p-4 space-y-3" data-testid="voice-compare">
      <div className="font-bold text-sm flex items-center gap-2"><Columns2 className="w-4 h-4 text-primary" /> مقارنة الأصوات بنفس الجملة</div>
      <p className="text-xs text-muted-foreground">اختر صوتين أو أكثر (الصوت المدرّب يظهر بنسخته الأساسية والمستنسخة)، اكتب جملة، واضغط قارن.</p>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <button key={o.key} type="button" onClick={() => toggle(o.key)} data-testid={`compare-pick-${o.key}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${picked.includes(o.key) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{o.label}</button>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="bg-secondary border-border" data-testid="compare-text" />
      <Button onClick={run} disabled={busy} className="gap-2" data-testid="compare-run-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} قارن</Button>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(res).map(([k, r]) => (
          <div key={k} className="border border-border rounded-lg p-3 space-y-2" data-testid={`compare-result-${k}`}>
            <div className="text-xs font-bold">{r.label}</div>
            {r.busy ? <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> جارٍ التوليد…</div>
              : r.error ? <div className="text-xs text-destructive">{r.error}</div>
              : <audio src={r.url} controls className="w-full h-9" />}
          </div>
        ))}
      </div>
    </div>
  );
};
